import { useParams } from "react-router";
import PageHeader from "../../components/PageHeader";
import { ROUTES } from "../../constants/routes.constant";
import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";

import { deleteCall, getCall } from "../../constants/apicalls.constant";
import { DiscountType } from "../../models/discount";
import { Flex, Table, Button, message, Modal, Descriptions, Spin } from "antd";

import { TableProps, Popconfirm } from "antd";
import { IconEye, IconTrash } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import getCurrencyWithSymbol from "../../utils/getCurrencySymbol";
import {
  formatDiscountUsageLimit,
  getDiscountUsedCount,
} from "../../utils/discountDisplay";
import { useState } from "react";

const DiscountList = () => {
  const { id: eventId } = useParams();
  const event = useSelector((state: RootState) => state.event);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedDiscount, setSelectedDiscount] = useState<DiscountType | null>(
    null,
  );

  const { response, loading, error, trigger } = useAPI<{
    message: string;
    status: boolean;
    data: { data: DiscountType[] };
  }>(
    ENDPOINTS.discount,
    true,
    [eventId],
    getCall({
      filter: {
        event_id: {
          eq: eventId,
        },
      },
    }),
  );

  const { trigger: fetchSingleDiscount, loading: loadingView } = useAPI<{
    message: string;
    status: boolean;
    data: DiscountType;
  }>(ENDPOINTS.discount);

  const onDelete = async (id: string) => {
    const res = await trigger(deleteCall(id, { list: true }));
    if (res?.status) {
      message.success(res?.message);
    } else message.error("something went wrong");
  };
  const handleView = async (id: string | undefined) => {
    setIsModalOpen(true);
    const res = await fetchSingleDiscount(
      getCall({ with: ["conditions"] }, id),
    );
    if (res?.status) {
      setSelectedDiscount(res.data);
    } else {
      message.error("Failed to fetch discount details");
    }
  };
  const columns: TableProps<DiscountType>["columns"] = [
    {
      title: "Coupon Code",
      dataIndex: "coupon_code",
      key: "coupon_code",
    },
    {
      title: "Coupon Type",
      dataIndex: "type",
      key: "type",
      render: (value) => (
        <span className="capitalize">
          {value === "fixed_amount" ? "Fixed Amount" : value}
        </span>
      ),
    },
    {
      title: "Coupon Value",
      dataIndex: "value",
      key: "value",
      render: (value, record) => (
        <span className="capitalize">
          {record.type === "percentage"
            ? `${value}%`
            : getCurrencyWithSymbol(event?.currency, Number(value))}
        </span>
      ),
    },
    {
      title: "Visibility",
      dataIndex: "is_exclusive",
      key: "is_exclusive",
      render: (value: boolean) => (
        <span>{value ? "Private" : "Public"}</span>
      ),
    },
    {
      title: "Usage Limit",
      dataIndex: "usage_limit",
      key: "usage_limit",
      render: (_, record) => (
        <span>
          {formatDiscountUsageLimit(
            record as DiscountType & Record<string, unknown>,
          )}
        </span>
      ),
    },
    {
      title: "Used Now",
      key: "usage_count",
      render: (_, record) => (
        <span>
          {getDiscountUsedCount(
            record as DiscountType & Record<string, unknown>,
          )}
        </span>
      ),
    },
    {
      title: (
        <Flex justify="center">
          <label>Action</label>
        </Flex>
      ),
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Flex gap={10} justify="center" align="center">
          <Popconfirm
            title="Are you sure you want to delete this event?"
            onConfirm={() => {
              if (record?.id) onDelete(record?.id);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<IconTrash size={16} color="red" />} />
          </Popconfirm>
          <Button
            type="text"
            icon={<IconEye size={16} color="black" />}
            onClick={() => handleView(record.id)}
          />
        </Flex>
      ),
    },
  ];
  if (error) return <>Something went wrong</>;
  return (
    <div className="p-[24px]">
      <PageHeader
        title="Coupon"
        backBtn={false}
        description="Track and manage the discounts you've set up."
        additionalButton={[
          {
            name: "Add Coupon",
            route: ROUTES.ADD_DISCOUNT.path.replace(":id", eventId!),
          },
        ]}
      />

      <Table<DiscountType>
        scroll={{ x: "max-content" }}
        rowKey={"id"}
        columns={columns}
        loading={loading}
        dataSource={response?.data?.data}
        bordered={true}
      />

      <Modal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setTimeout(() => setSelectedDiscount(null), 300);
        }}
        footer={null}
        title="Discount Details"
      >
        {loadingView ? (
          <Flex justify="center" align="center" style={{ minHeight: "200px" }}>
            <Spin size="large" />
          </Flex>
        ) : selectedDiscount ? (
          <Descriptions
            column={1}
            bordered
            size="small"
            style={{ marginTop: 20 }}
          >
            <Descriptions.Item label="Coupon Code">
              {selectedDiscount.coupon_code}
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <span className="capitalize">{selectedDiscount.type}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Value">
              {selectedDiscount.type === "percentage"
                ? `${selectedDiscount.value}%`
                : getCurrencyWithSymbol(
                    event?.currency,
                    Number(selectedDiscount.value),
                  )}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <span className="capitalize">{selectedDiscount.status}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Usage Limit">
              {formatDiscountUsageLimit(selectedDiscount as any)}
            </Descriptions.Item>
            <Descriptions.Item label="Used Now">
              {getDiscountUsedCount(selectedDiscount as any)}
            </Descriptions.Item>
            <Descriptions.Item label="Start Date/Time">
              {selectedDiscount.start_date} {selectedDiscount.start_time}
            </Descriptions.Item>
            {selectedDiscount.end_date && (
              <Descriptions.Item label="End Date/Time">
                {selectedDiscount.end_date} {selectedDiscount.end_time}
              </Descriptions.Item>
            )}
            {selectedDiscount.description && (
              <Descriptions.Item label="Description">
                {selectedDiscount.description}
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <Flex justify="center" align="center" style={{ minHeight: "200px" }}>
            <p>No data found.</p>
          </Flex>
        )}
      </Modal>
    </div>
  );
};

export default DiscountList;
